/** @module csi/prefs */
define([
	"dojo/_base/lang",
	"underscore",
	"csi",
	"csi/comm/PostRequest",
	"csi/prefs/_base",
	"csi/prefs/Defaults",
	"csi/state",
	"csi/state/rootStore",
	"csi/store/_base",
	"csi/store/CustomViews",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Search"
], function(lang, _, csi, PostRequest, csiPrefs, Defaults, state, {sessionStore}, csiStore, CustomViews, Names, xml, Search) {

	// type CustomViewStoreCallback = (view: ViewWidget, modifiedUrls: cnl[]) => void;
	// Record<string, CustomViewStoreCallback[]> - record of observers where key is the custom view container key, and the value is a list of callbacks to call when the store is changed
	const _storeObservers = {};

	// {String:CustomViews}: object holding stores by preference key
	var _stores = {};

	// Defaults: object used to calculate default custom views for user and for site
	var _defaults;
	var _siteDefaults;

	// Object: map of view context to array of handles for watching changes to current user's selected custom view
	var _watches = {};

	function _canSelectView(/*Boolean*/bSiteDefault, /*Any*/unused, /*PreferenceView?*/item) {
		// summary: return if view can be accessed by current user; bSiteDefault determines whether or
		// not we allow the OOTB default view (for example, in custom view dialog it is allowed, but not
		// in toolbar control to select a view).
		if (bSiteDefault && (item.urlNode == csi.EMPTY_URL)) {
			// if there is a site default, then do not allow OOTB default view
			return false;
		} else if (!state.get("admin") || item.isSystemWide()) {
			// a non-admin user can select all views because the store only queries for ones they have access to,
			// and all site level views are visible if not already ruled out
			return true;
		} else if (!item.isShared()) {
			// if user view, then only allow if view is for current user
			return item.isCurrentUser();
		}

		// an admin can use any role view
		return true;
	}

	/**
	 * Remove watch handle
	 * @param {Object} handle
	 */
	function _removeWatch(handle) {
		var a = _watches[handle.context];
		if (a) {
			a.dele(handle);
		}
	}

	/**
	 * Return true if user can view given custom view
	 * @param {PreferenceView} customView
	 * @param {boolean} bSiteDefault
	 * @return {boolean}
	 */
	function canSelectView(customView, bSiteDefault) {
		return _canSelectView(bSiteDefault, null, customView);
	}

	/**
	 * Construct Names store of available custom views for given view; by default only system-wide views will be included
	 * @param {string} keyView - key for custom view container
	 * @param {string} [urlRole] - if role provided, then add custom views of that role
	 * @param {string} [urlUser] - if user provided, then add personal custom views of that user
	 * @param {Node} [selectedCustomView] - previously selected custom view from store
	 * @return {Names}
	 */
	function getCustomViewNamesStore(keyView, urlRole, urlUser, selectedCustomView) {
		const data = [];
		const defaultUrl = getDefaultCustomViewUrl(keyView, urlRole);
		const search = new Search().byType(csiPrefs.TYPE_VIEW);
		const query = search.getQuery();
		const umentry = csiPrefs.ATTR_UMENTRY;

		search.setLimitAttribute(csiPrefs.getContainerUrl(keyView), csiPrefs.ATTR_PREFERENCES);

		if (xml.isValidURL(urlRole)) {
			query.Or()
				.RefAttr(umentry, xml.OP_EQUALS, urlRole)
				.Attr2(umentry, xml.OP_EQUALS, {Dimension: 0});
		} else {
			query.Attr2(umentry, xml.OP_EQUALS, {Dimension: 0});
		}

		// pre-populate data - if there is a system-wide or role default view, then do not add empty entry
		if (!xml.isValidURL(defaultUrl)) {
			data.push(csiStore.nameObject(csiPrefs.getDefaultViewName()));
		}

		if (selectedCustomView) {
			data.push(csiStore.nameObject(selectedCustomView));
		}

		// override emptyValue for store because the "Default" view should be viewed as a custom view like any other,
		// and also ignoreInvalid so the "Default view will not be viewed as invalid since it is not part of the request response
		return new Names({
			data,
			defaultValue: defaultUrl,
			emptyValue: "",
			ignoreInvalid: true,
			request: search.getRequest(),
			typeNode: csiPrefs.TYPE_VIEW
		});
	}

	/**
	 * Return object holding default custom views for current user
	 * @param {boolean} siteOnly - only consider system-wide default views
	 * @return {Defaults}
	 */
	function getDefaults(siteOnly) {
		if (siteOnly) {
			if (!_siteDefaults) {
				_siteDefaults = new Defaults();
				_siteDefaults.setSitePreferences(sessionStore.sitePreferences);
			}

			return _siteDefaults;
		}

		if (!_defaults) {
			_defaults = new Defaults();
			_defaults.watches = _watches;
			_defaults.setSitePreferences(sessionStore.sitePreferences);
			_defaults.setUserPreferences(sessionStore.userPreferences);

			var rp = sessionStore.getRolePreferences();
			if (rp) {
				_defaults.setRolePreferences(rp);
			}
		}

		return _defaults;
	}

	/**
	 * Return default custom view URL if there is a site and/or role default custom view defined for the given view
	 * @param {string} keyView - key for custom view container
	 * @param {string} [urlRole] - if role provided, then add custom views of that role
	 * @return {string}
	 */
	function getDefaultCustomViewUrl(keyView, urlRole) {
		const defaults = new Defaults();
		defaults.setSitePreferences(sessionStore.sitePreferences);

		const rp = xml.isValidURL(urlRole) ? sessionStore.getRolePreferences(urlRole) : null;
		if (rp) {
			defaults.setRolePreferences(rp);
		}

		return defaults.get(keyView);
	}

	/**
	 * Notify watchers of store that a custom view store has changed
	 * @param {string} keyView
	 * @param {ViewWidget} view
	 * @param {cnl[]} modifiedUrls
	 */
	function notifyCustomViewStoreChanged(keyView, view, modifiedUrls) {
		_storeObservers[keyView]?.forEach((cb) => cb(view, modifiedUrls));
	}

	/**
	 * Notify watchers of store that a custom view store has changed
	 * @param {string} keyView
	 * @param {CustomViewStoreCallback} callback
	 * @return {RemoveEventHandle}
	 */
	function observeCustomViewStore(keyView, callback) {
		return csi.objectArrayAddValue(_storeObservers, keyView, callback);
	}

	return {
		...csiPrefs,
		canSelectView,
		getCustomViewNamesStore,
		getDefaults,
		notifyCustomViewStoreChanged,
		observeCustomViewStore,

		/**
		 * Return default custom view for site and role based on given context
		 */
		getSiteRolesDefaults: function() {
			const defaults = new Defaults();
			defaults.setSitePreferences(sessionStore.sitePreferences);
			const rp = sessionStore.getRolePreferences();
			if (rp) {
				defaults.setRolePreferences(rp);
			}
			return defaults;
		},

		/**
		 * Return store containing custom views for view with given key
		 * @param {string} keyView
		 * @param {PreferenceView} defaultView
		 * @param {boolean} [bReset]
		 * @return {CustomViews}
		 */
		getStore: function(keyView, defaultView, bReset) {
			// FIX: do we need cache for these stores / nodes anymore?
			let store = bReset ? null : _stores[keyView];

			if (store) {
				if (!defaultView) {
					store.setDefaultView(defaultView);
				} else if (defaultView != store.defaultView) {
					store = null;
				}
			}

			if (!store) {
				const containerUrl = csiPrefs.getContainerUrl(keyView);
				const {playableRolesStore} = sessionStore;
				let partyUrls;

				// NOTE: use same cache as that used with csi/resolver to match code in PreferencesController;
				// if we want to start using "cache", then need to change the controller code too
				const partyCache = state.get("nonObservableCache");

				if (!state.get("admin")) {
					partyUrls = _.pluck(playableRolesStore.internalQuery(), playableRolesStore.idProperty);
					partyUrls.push(csi.EMPTY_URL);
					partyUrls.push(state.get("userUrl"));
				}

				const request = new PostRequest("SiteAdmin", "GetAvailableCustomViews", {
					"URL": containerUrl,
					"UMEntries": partyUrls
				});

				store = _stores[keyView] = new CustomViews({
					containerUrl,
					data: [defaultView],
					defaultView,
					partyCache,
					playableRolesStore,
					request
				});
			}

			return store;
		},

		getStoreSelectQuery: function(/*String*/idContext) {
			// summary: return query object to use when selecting custom views just for the current user
			return {
				canSelect: {
					test: lang.partial(_canSelectView, this.hasSiteDefaultForContext(idContext))
				}
			};
		},

		hasSiteDefaultForContext: function(/*String*/idContext) {
			// summary: return true if site default view for given context
			return !!getDefaults().get(idContext, csi.EMPTY_URL, true);
		},

		releaseStore: function(/*CustomViews*/store) {
			// summary: release item references to transient data
			store.setDefaultView(null);
		},

		/**
		 * Reset defaults so new defaults will be created on-demand next time needed
		 * @param {boolean} [useCache] - do not request new data, just use what is in the cache
		 * @return {Promise<JsonResponse>}
		 */
		resetDefaults(useCache) {
			_defaults = null;
			return useCache ? null : sessionStore.resetPreferences();
		},

		resetStores: function() {
			// summary: reset preference stores
			_stores = {};
		},

		watchDefaultView: function(/*String*/idContext, /*Function*/callback) {
			// summary: add watch for given context for current user
			var handle = {
				context: idContext,
				func: callback
			};

			handle.remove = lang.partial(_removeWatch, handle);

			csi.objectArrayAddValue(_watches, idContext, handle);

			return handle;
		},
	};
});